"use client";

import { useFieldArray, useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { PlusCircle } from "lucide-react";

import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion } from "@/components/ui/accordion";
import OptionGroupCard from "./OptionGroupCard";
import { AdditionalSelectionType } from "@/interfaces/products";

export default function AddonsStep() {
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "additional",
  });

  const [openItems, setOpenItems] = useState<string[]>(
    fields.map((_, index) => `item-${index}`)
  );

  useEffect(() => {
    setOpenItems(fields.map((_, index) => `item-${index}`));
  }, [fields]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active.id !== over?.id && over) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over.id);
      move(oldIndex, newIndex);
    }
  }

  return (
    <CardContent className="p-6 space-y-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={fields} strategy={verticalListSortingStrategy}>
          <Accordion
            type="multiple"
            value={openItems}
            onValueChange={(val) => setOpenItems(val as string[])}
            className="w-full space-y-4"
          >
            {fields.map((field, index) => (
              <OptionGroupCard
                key={field.id}
                groupIndex={index}
                removeGroup={remove}
                id={field.id}
              />
            ))}
          </Accordion>
        </SortableContext>
      </DndContext>

      <Button
        type="button"
        variant="outline"
        className="w-full border-dashed bg-transparent"
        onClick={() =>
          append({
            name: `Novo Grupo ${fields.length + 1}`,
            selectionType: AdditionalSelectionType.CHECKBOX,
            minQuantity: 0,
            maxQuantity: 1,
            items: [],
          })
        }
      >
        <PlusCircle className="mr-2 h-4 w-4" />
        Adicionar Grupo de Adicionais
      </Button>
    </CardContent>
  );
}
